import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend,
} from 'recharts';
import {
  MapPin, BarChart3, PieChart as PieChartIcon, TrendingUp,
  Gavel, Activity, Clock, XCircle, Star, Zap, Building2,
  GitBranch, Layers,
} from 'lucide-react';
import i18n from '../../../../i18n';
import { getLoggedUser } from '../../../../state/user';
import { SideBar } from '../../sidebar';
import HeaderBanner from '../../../common/HeaderBanner';
import FilterSection from '../../../common/Filter';
import { getAuctionStatistics } from './services/auctionStatisticsService';
import '../../../../styles/pages/auction-statistics.scss';

const user = getLoggedUser();

const STATUS_COLORS = {
  'Active': '#10b981',
  'Expired': '#f59e0b',
  'Upcoming': '#6366f1',
  'Cancelled': '#ef4444',
  'Featured': '#ec4899',
  'Not Approved': '#94a3b8',
  'Opened': '#335797',
};

const DELIVERY_COLORS = [
  '#f59e0b', '#10b981', '#ef4444', '#3b82f6',
  '#8b5cf6', '#14b8a6', '#f97316', '#06b6d4', '#22c55e',
];

const OVERVIEW_CONFIG = [
  { key: 'total', label: 'total_auctions', icon: Gavel, variant: 'total' },
  { key: 'active', label: 'active', icon: Activity, variant: 'active' },
  { key: 'expired', label: 'expired', icon: Clock, variant: 'expired' },
  { key: 'upcoming', label: 'upcoming', icon: TrendingUp, variant: 'upcoming' },
  { key: 'featured', label: 'featured', icon: Star, variant: 'featured' },
];

class AuctionStatistics extends React.Component {
  constructor() {
    super();
    this.state = {
      search: { startDate: null, endDate: null },
      data: null,
      isLoading: false,
      hideSidebar: false,
      mapLoaded: false,
    };
    this.mapRef = React.createRef();
    this.mapInstance = null;
    this.markers = [];
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(window.location.search);
    const search = {
      startDate: params.get('startDate') || null,
      endDate: params.get('endDate') || null,
    };
    this.setState({ search }, () => this.fetchStatistics());
  }

  componentWillUnmount() {
    this.clearMarkers();
  }

  async fetchStatistics() {
    this.setState({ isLoading: true });
    const { startDate, endDate } = this.state.search;
    const q = {};
    if (startDate) q.startDate = startDate;
    if (endDate) q.endDate = endDate;

    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(q).filter(([_, v]) => v))
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}${queryString ? '?' + queryString : ''}`);

    const response = await getAuctionStatistics(q);
    if (response && response.success) {
      this.setState({ data: response.data, isLoading: false }, () => {
        this.initMap();
      });
    } else {
      this.setState({ isLoading: false });
    }
  }

  // ─── Leaflet Map ───
  initMap() {
    const pins = this.state.data?.map_pins || [];
    if (!pins.length || !this.mapRef.current) return;

    // Lazy load leaflet CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Lazy load leaflet JS
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => this.renderMap(pins);
      document.head.appendChild(script);
    } else {
      this.renderMap(pins);
    }
  }

  renderMap(pins) {
    const L = window.L;
    if (!L || !this.mapRef.current) return;

    // Destroy previous instance
    if (this.mapInstance) {
      this.mapInstance.remove();
      this.mapInstance = null;
    }

    // Default center: Oman
    const center = pins.length > 0 ? [pins[0].lat, pins[0].lng] : [23.58, 58.38];

    this.mapInstance = L.map(this.mapRef.current, {
      scrollWheelZoom: false,
    }).setView(center, 7);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(this.mapInstance);

    this.clearMarkers();

    pins.forEach((pin) => {
      const radius = Math.min(Math.max(pin.auction_count * 3, 8), 30);

      const marker = L.circleMarker([pin.lat, pin.lng], {
        radius: radius,
        fillColor: '#335797',
        color: '#fff',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.7,
      }).addTo(this.mapInstance);

      marker.bindPopup(`
        <div class="stats-map-popup">
          <div class="stats-map-popup__title">${pin.location || 'Unknown'}</div>
          <div class="stats-map-popup__row"><span>Total</span><span>${pin.auction_count}</span></div>
          <div class="stats-map-popup__row"><span>Active</span><span>${pin.active_count || 0}</span></div>
          <div class="stats-map-popup__row"><span>Expired</span><span>${pin.expired_count || 0}</span></div>
        </div>
      `);

      // Count label
      const icon = L.divIcon({
        className: 'stats-map-count-icon',
        html: `<span style="
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          width: ${radius * 2}px;
          height: ${radius * 2}px;
          pointer-events: none;
        ">${pin.auction_count}</span>`,
        iconSize: [radius * 2, radius * 2],
        iconAnchor: [radius, radius],
      });
      const label = L.marker([pin.lat, pin.lng], { icon, interactive: false }).addTo(this.mapInstance);

      this.markers.push(marker, label);
    });

    // Fit bounds
    if (pins.length > 1) {
      const bounds = L.latLngBounds(pins.map((p) => [p.lat, p.lng]));
      this.mapInstance.fitBounds(bounds, { padding: [40, 40] });
    }

    this.setState({ mapLoaded: true });
  }

  clearMarkers() {
    this.markers.forEach((m) => m.remove());
    this.markers = [];
  }

  handleSearchChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      search: { ...prev.search, [name]: value },
    }));
  };

  handleSearch = () => this.fetchStatistics();

  handleClear = () => {
    this.setState({ search: { startDate: null, endDate: null } }, () => this.fetchStatistics());
  };

  // ─── Render helpers ───

  renderOverviewCards() {
    const { data, isLoading } = this.state;
    if (isLoading) {
      return (
        <div className="stats-overview-grid">
          {[...Array(7)].map((_, i) => <div key={i} className="stats-skeleton-card" />)}
        </div>
      );
    }
    if (!data) return null;

    return (
      <div className="stats-overview-grid">
        {OVERVIEW_CONFIG.map((cfg) => {
          const Icon = cfg.icon;
          const count = data.overview?.[cfg.key] ?? 0;
          return (
            <div key={cfg.key} className={`stats-overview-card stats-overview-card--${cfg.variant}`}>
              <div className={`stats-overview-card__icon stats-overview-card__icon--${cfg.variant}`}>
                <Icon size={18} />
              </div>
              <div className="stats-overview-card__value">{count}</div>
              <div className="stats-overview-card__label">{i18n.t(cfg.label)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  renderStatusPie() {
    const items = this.state.data?.status_breakdown || [];
    if (!items.length) return null;

    return (
      <div className="stats-chart-card">
        <div className="stats-chart-card__header">
          <div>
            <div className="stats-chart-card__title"><PieChartIcon size={18} /> {i18n.t('status_breakdown')}</div>
            <div className="stats-chart-card__subtitle">{i18n.t('auctions_by_status')}</div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={items}
              dataKey="count"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={100}
              paddingAngle={3}
              label={({ label, count }) => `${label} (${count})`}
            >
              {items.map((entry, i) => (
                <Cell key={i} fill={STATUS_COLORS[entry.label] || '#94a3b8'} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  renderDeliveryBar() {
    const items = this.state.data?.delivery_breakdown || [];
    if (!items.length) return null;

    return (
      <div className="stats-chart-card">
        <div className="stats-chart-card__header">
          <div>
            <div className="stats-chart-card__title"><BarChart3 size={18} /> {i18n.t('delivery_status')}</div>
            <div className="stats-chart-card__subtitle">{i18n.t('expired_auctions_by_delivery_status')}</div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={items} barSize={32}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="count" radius={[6, 6, 0, 0]}>
              {items.map((_, i) => (
                <Cell key={i} fill={DELIVERY_COLORS[i % DELIVERY_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  renderMonthlyTrend() {
    const items = this.state.data?.monthly_trend || [];
    if (!items.length) return null;

    return (
      <div className="stats-chart-card stats-chart-card--full">
        <div className="stats-chart-card__header">
          <div>
            <div className="stats-chart-card__title"><TrendingUp size={18} /> {i18n.t('monthly_trend')}</div>
            <div className="stats-chart-card__subtitle">{i18n.t('auctions_created_last_12_months')}</div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={items}>
            <defs>
              <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#335797" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#335797" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip />
            <Area type="monotone" dataKey="count" stroke="#335797" strokeWidth={2} fill="url(#areaGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  renderBreakdownCard(title, icon, items) {
    const Icon = icon;
    return (
      <div className="stats-breakdown-card">
        <div className="stats-breakdown-card__header">
          <Icon size={18} />
          <h4>{title}</h4>
        </div>
        {items && items.length > 0 ? (
          <ul className="stats-breakdown-list">
            {items.map((item, i) => (
              <li key={i} className="stats-breakdown-item">
                <span className="stats-breakdown-item__name">{item.name || item.location || '—'}</span>
                <span className="stats-breakdown-item__count">{item.count}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="stats-breakdown-empty">{i18n.t('no_data_available')}</div>
        )}
      </div>
    );
  }

  renderTopLocations() {
    const items = this.state.data?.top_locations || [];
    if (!items.length) return null;

    return (
      <div className="stats-top-locations">
        <div className="stats-chart-card__header">
          <div>
            <div className="stats-chart-card__title"><MapPin size={18} /> {i18n.t('top_locations_by_bid')}</div>
            <div className="stats-chart-card__subtitle">{i18n.t('highest_value_auction_locations')}</div>
          </div>
        </div>
        <div className="stats-top-locations__row stats-top-locations__row--header">
          <span>#</span>
          <span>{i18n.t('location')}</span>
          <span style={{ textAlign: 'center' }}>{i18n.t('auctions')}</span>
          <span style={{ textAlign: 'right' }}>{i18n.t('total_bids')}</span>
        </div>
        {items.map((item, i) => (
          <div key={i} className="stats-top-locations__row">
            <div className="stats-top-locations__rank">{i + 1}</div>
            <span>{item.location || '—'}</span>
            <span className="stats-top-locations__count">{item.auction_count}</span>
            <span className="stats-top-locations__amount">{Number(item.total_bid_amount).toFixed(3)}</span>
          </div>
        ))}
      </div>
    );
  }

  renderMap() {
    const pins = this.state.data?.map_pins || [];

    return (
      <div className="stats-map-container">
        <div className="stats-map-header">
          <div className="stats-map-title"><MapPin size={20} /> {i18n.t('auction_locations')}</div>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{pins.length} {i18n.t('locations')}</span>
        </div>
        <div className="stats-map-wrapper" ref={this.mapRef}>
          {!pins.length && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
              {i18n.t('no_location_data')}
            </div>
          )}
        </div>
        <div className="stats-map-legend">
          <div className="stats-map-legend__item">
            <div className="stats-map-legend__dot stats-map-legend__dot--active" />
            <span>{i18n.t('active')}</span>
          </div>
          <div className="stats-map-legend__item">
            <div className="stats-map-legend__dot stats-map-legend__dot--expired" />
            <span>{i18n.t('expired')}</span>
          </div>
          <div className="stats-map-legend__item">
            <div className="stats-map-legend__dot stats-map-legend__dot--other" />
            <span>{i18n.t('total')}</span>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { data, isLoading, search } = this.state;

    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('auction_statistics')}
          subtitle={i18n.t('auction_statistics_subtitle')}
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">
                {!this.state.hideSidebar && <SideBar />}
              </div>
              <div className={this.state.hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'}>
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !this.state.hideSidebar })}
                      >
                        {this.state.hideSidebar ? <i className="icon-size-actual" /> : <i className="icon-size-fullscreen" />}
                      </span>
                    </div>
                  </div>
                  <div className="card-body">
                    <FilterSection loading={isLoading} onSearch={this.handleSearch} onClear={this.handleClear}>
                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="startDate" type="date" name="startDate"
                          value={search.startDate || ''}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="startDate" className="floating-label">{i18n.t('start_date')}</label>
                      </div>
                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="endDate" type="date" name="endDate"
                          value={search.endDate || ''}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="endDate" className="floating-label">{i18n.t('end_date')}</label>
                      </div>
                    </FilterSection>

                    {/* Overview Cards */}
                    {this.renderOverviewCards()}

                    {/* Charts */}
                    {isLoading ? (
                      <div className="stats-charts-grid">
                        <div className="stats-skeleton-chart" />
                        <div className="stats-skeleton-chart" />
                        <div className="stats-skeleton-chart" style={{ gridColumn: '1 / -1' }} />
                      </div>
                    ) : data && (
                      <>
                        <div className="stats-charts-grid">
                          {this.renderStatusPie()}
                          {this.renderDeliveryBar()}
                          {this.renderMonthlyTrend()}
                        </div>

                        {/* Interactive Map */}
                        {/* {this.renderMap()} */}

                        {/* Top Locations */}
                        {this.renderTopLocations()}

                        {/* Breakdown Tables */}
                        <div className="stats-breakdown-grid">
                          {this.renderBreakdownCard(i18n.t('by_department'), Building2, data.by_department)}
                          {this.renderBreakdownCard(i18n.t('by_subdivision'), GitBranch, data.by_subdivision)}
                          {this.renderBreakdownCard(i18n.t('by_location'), Layers, data.by_location)}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export default AuctionStatistics;